import {
  type NodeItem,
  selectBlockParent,
  selectBlocksByNode,
  selectNodeChildren,
  selectNodeDescentChildren,
  selectNodeParent,
  selectNodesBySection,
  selectSectionsByTab,
  selectTabNodes,
} from '@octopus-do/octopus-project-core'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import type { AppState } from '@store/types'
import { useStore } from 'react-redux'

import type { ExportData } from '@/hooks/exportDropdown/types'

// Export data is assembled from the store at export time, not subscribed to.
// The hook is mounted for the whole artboard session through the project menu,
// and a subscribed version rebuilt the entire page/block tree on every dispatch,
// including every zoom step (OCT-5072).
const getSectionIds = (state: AppState) =>
  selectSectionsByTab(migrationProjectSelector.getActiveOrFirstTabId(state))(
    state
  )

const getExportData = (state: AppState): ExportData[][] => {
  const activeTabId = migrationProjectSelector.getActiveOrFirstTabId(state)

  const nodeItems: NodeItem[] = selectTabNodes(activeTabId)({
    project: state.manager.project,
    relations: state.relations,
  })

  return getSectionIds(state).map((item) => {
    const nodeIds = selectNodesBySection(item)(state).reduce((acc, id) => {
      const descends = selectNodeDescentChildren(id)(state)
      const filteredDescends = descends.filter(
        (descend) => !acc.includes(descend)
      )
      return [...acc, ...filteredDescends]
    }, [] as string[])

    const buildTree = (id: string): any => {
      const childIds = selectNodeChildren(id)(state)
      const blockIds = selectBlocksByNode(id)(state)
      const parentId = selectNodeParent(id)(state)[0] || null

      const blocks = blockIds.map((id) => ({
        ...migrationProjectSelector.getProjectBlockOrSymbolData(id)(state),
        parentId: selectBlockParent(id)(state)[0],
        nodeId: id,
      }))

      const node = nodeItems.find((t) => t.id === id)

      return {
        ...node,
        title: node?.title || 'Page',
        parentId,
        blocks,
        children: childIds.map(buildTree),
      }
    }

    const childSet = new Set()
    nodeIds.forEach((id) => {
      selectNodeChildren(id)(state).forEach((childId) => {
        childSet.add(childId)
      })
    })

    const rootNodes = nodeIds.filter((id) => !childSet.has(id))
    return rootNodes.map(buildTree)
  })
}

const flattenExportData = (exportData: ExportData[][]): ExportData[] => {
  const result: ExportData[] = []

  function processPage(d: ExportData) {
    result.push(d)

    if (d.children && d.children.length > 0) {
      d.children.forEach((child) => {
        processPage(child)
      })
    }
  }

  exportData.forEach((group) => {
    group.forEach((page) => {
      processPage(page)
    })
  })

  return result
}

export const useGetDataForExport = () => {
  const store = useStore<AppState>()

  return {
    getExportData: () => getExportData(store.getState()),
    getSectionIds: () => getSectionIds(store.getState()),
    getFlattenExportData: () =>
      flattenExportData(getExportData(store.getState())),
  }
}
